import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Typography } from 'antd';
import { useAdminUpdatePassword } from 'all-in-one-shared-api';

import forgotIcon from 'assets/images/reset-icon.svg';
import { object, ref, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { FormWrapper, FormInput } from 'components/form';
import styled from 'styled-components';
import { validatePassword } from 'all-in-one-shared-api';
import { FormInputPassword } from 'components/form/FormInputPassword';

interface AdminPassword {
  username: string;
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledCard = styled(Card)`
  width: 450px;
  border-radius: 8px;
  padding: 0 24px;
  margin-top: 16px;
`;

const ItemWrapper = styled.div`
  margin-bottom: 12px;
`;

function UpdateAdminPasswordPage() {
  const { t } = useTranslation();

  const [openAlert, setOpenAlert] = React.useState(false);
  const [snackbarState, setSnackbarState] = React.useState<'success' | 'error'>('success');
  const [snackbarMessageKey, setSnackbarMessageKey] = React.useState('');
  const navigate = useNavigate();
  const { state } = useLocation();
  const routeState = state as AdminPassword;

  useEffect(() => {
    if (!routeState?.username) {
      navigate('/login');
    }
  }, [navigate, routeState?.username]);

  const getAdminPasswordSchema = () =>
    object({
      username: string().required(t('changePassword.error.required')),
      oldPassword: string().required(t('changePassword.error.required')),
      newPassword: string()
        .required(t('changePassword.error.required'))
        .test(
          'newPasswordTest',
          t('changePassword.error.invalidPassword'),
          (value) => validatePassword(value)?.isValid,
        ),
      confirmNewPassword: string()
        .required(t('changePassword.error.required'))
        .oneOf([ref('newPassword')], t('changePassword.error.invalidConfirmPassword')),
    });

  const methods = useForm<AdminPassword>({
    resolver: yupResolver(getAdminPasswordSchema()),
    defaultValues: { username: routeState?.username },
  });

  const { shouldFetch } = useAdminUpdatePassword(
    'useAdminUpdatePassword',
    methods.getValues('username'),
    methods.getValues('oldPassword'),
    methods.getValues('newPassword'),
    {
      onSuccess: () => {
        shouldFetch(false);
        navigate('/login', { replace: true });
      },
      onError: () => {
        shouldFetch(false);
        setSnackbarState('error');
        setSnackbarMessageKey('auth.login.failed');
        setOpenAlert(true);
      },
      shouldRetryOnError: false,
    },
  );

  const handleSubmit = () => {
    shouldFetch(true);
  };

  const handleCloseSnackbar = () => {
    setOpenAlert(false);
  };

  return (
    <FormProvider {...methods}>
      <form data-testid="updateAdminPasswordPage" onSubmit={methods.handleSubmit(handleSubmit)}>
        <Wrapper>
          <img src={forgotIcon} alt="forgot-icon" style={{ marginBottom: 12 }} />
          <Typography.Title level={3}>{t('resetPassword.resetPasswordButton')}</Typography.Title>
          <div>
            <StyledCard>
              {openAlert && (
                <Alert
                  message={t(snackbarMessageKey)}
                  type={snackbarState}
                  closable
                  onClose={handleCloseSnackbar}
                  style={{ marginBottom: 16 }}
                />
              )}
              <ItemWrapper>
                <FormWrapper name="username" label={t('changePassword.usernameLabel')}>
                  <FormInput name="username" placeholder={t('changePassword.usernameTextPlaceholder')} />
                </FormWrapper>
              </ItemWrapper>
              <ItemWrapper>
                <FormWrapper name="oldPassword" label={t('changePassword.currentPasswordLabel')}>
                  <FormInputPassword
                    name="oldPassword"
                    placeholder={t('changePassword.currentPasswordTextPlaceholder')}
                  />
                </FormWrapper>
              </ItemWrapper>
              <ItemWrapper>
                <FormWrapper name="newPassword" label={t('changePassword.newPasswordLabel')}>
                  <FormInputPassword name="newPassword" placeholder={t('changePassword.newPasswordTextPlaceholder')} />
                </FormWrapper>
              </ItemWrapper>
              <ItemWrapper>
                <FormWrapper name="confirmNewPassword" label={t('changePassword.confirmPasswordLabel')}>
                  <FormInputPassword
                    name="confirmNewPassword"
                    placeholder={t('changePassword.confirmPasswordTextPlaceholder')}
                  />
                </FormWrapper>
              </ItemWrapper>
            </StyledCard>
            <Button
              data-testid="submitButton"
              block
              type="primary"
              size="large"
              htmlType="submit"
              style={{ marginTop: 16 }}
            >
              {t('changePassword.updatePasswordButton')}
            </Button>
          </div>
        </Wrapper>
      </form>
    </FormProvider>
  );
}

export default UpdateAdminPasswordPage;
